<template>
  <div>
    <!-- ══════════════════════════════════════════════════════════════
         AUDITORÍA DE VARIACIÓN DE PRECIOS
    ═══════════════════════════════════════════════════════════════ -->
    <div class="card shadow-sm">
      <div class="card-body">
        <!-- Loading -->
        <div v-if="loading" class="text-center my-5 py-5">
          <div class="spinner-border text-primary mb-3" role="status" style="width: 3rem; height: 3rem;"></div>
          <p class="text-muted">Analizando discrepancias de costos...</p>
        </div>

        <!-- Sin resultados -->
        <div v-else-if="elementosConVariacion.length === 0" class="alert alert-success d-flex align-items-center">
          <span class="material-symbols-outlined align-middle me-3" style="font-size: 28px;">check_circle</span>
          <div>
            <strong>¡Excelente!</strong><br>
            No se encontraron elementos con variaciones de precio en este trámite.
          </div>
        </div>

        <!-- Tabla de resultados -->
        <div v-else>
          <div class="table-responsive">
            <table class="table table-hover table-bordered align-middle">
              <thead class="table-light">
                <tr>
                  <th>Código</th>
                  <th>Descripción del Elemento</th>
                  <th class="text-center">Estado Planilla</th>
                  <th class="text-end">Valor Mínimo</th>
                  <th class="text-end">Valor Máximo</th>
                  <th class="text-center">Registros</th>
                  <th class="text-center" style="width: 90px">Acción</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in elementosConVariacion" :key="item.codigo">
                  <td>
                    <span class="badge bg-secondary">{{ item.codigo }}</span>
                  </td>
                  <td class="small fw-medium">{{ item.nombre }}</td>
                  <td class="text-center">
                    <span :class="getStatusClass(item.estadoPlanilla)">
                      {{ item.estadoPlanilla === 1 ? 'ACTIVO' : 'INACTIVO' }}
                    </span>
                  </td>
                  <td class="text-end text-success fw-bold">
                    ${{ item.min.toFixed(2) }}
                  </td>
                  <td class="text-end text-danger fw-bold">
                    ${{ item.max.toFixed(2) }}
                  </td>
                  <td class="text-center">
                    <span class="badge rounded-pill bg-info text-dark">{{ item.count }}</span>
                  </td>
                  <td class="text-center">
                    <button 
                      class="btn btn-sm btn-outline-primary"
                      @click="verDetalles(item)"
                      title="Ver detalles"
                    >
                      <span class="material-symbols-outlined" style="font-size: 18px;">search</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="mt-3 text-muted small">
            Mostrando {{ elementosConVariacion.length }} elementos con variación de precio
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";
import Swal from "sweetalert2";

export default {
  name: "AuditoriaPrecios",

  props: {
    idTramite: {
      type: [Number, String],
      required: true,
      default: 2
    }
  },

  data() {
    return {
      loading: false,
      elementosConVariacion: []
    };
  },

  mounted() {
    this.fetchData();
  },

  methods: {
    async fetchData() {
      this.loading = true;
      try {
        // Ejecutamos ambas peticiones en paralelo
        const [resPlanillas, resDetalles] = await Promise.all([
          api.get(`/vwplanillas/idtramite/${this.idTramite}`),
          api.get(`/vwplanilladets/idtramite/${this.idTramite}`)
        ]);

        const planillas = resPlanillas.data || [];
        const detalles = resDetalles.data || [];

        // Crear mapa de estados de planilla
        const mapaEstados = {};
        planillas.forEach(p => {
          mapaEstados[p.idPlanilla] = p.estado;
        });

        // Agrupar detalles por código de elemento
        const agrupado = {};
        detalles.forEach(det => {
          if (det.estado === 1) { // Solo detalles activos
            const cod = det.elementoCodigo;
            if (!agrupado[cod]) {
              agrupado[cod] = {
                codigo: cod,
                nombre: det.elementoNombre || 'Sin nombre',
                estadoPlanilla: mapaEstados[det.idPlanilla] || 0,
                precios: [],
                count: 0
              };
            }
            agrupado[cod].precios.push(Number(det.valorUni) || 0);
            agrupado[cod].count++;
          }
        });

        // Procesar y filtrar solo los que tienen variación
        this.elementosConVariacion = Object.values(agrupado)
          .map(item => ({
            ...item,
            min: Math.min(...item.precios),
            max: Math.max(...item.precios)
          }))
          .filter(item => item.max !== item.min)
          .sort((a, b) => b.count - a.count); // Ordenar por cantidad de registros

      } catch (error) {
        console.error("Error en auditoría de precios:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo procesar la auditoría de precios",
        });
      } finally {
        this.loading = false;
      }
    },

    getStatusClass(estado) {
      return estado === 1 
        ? 'badge bg-success-subtle text-success border border-success' 
        : 'badge bg-danger-subtle text-danger border border-danger';
    },

    verDetalles(item) {
      Swal.fire({
        title: `Análisis de ${item.codigo}`,
        html: `
          <strong>Elemento:</strong> ${item.nombre}<br><br>
          El precio varía entre <b>$${item.min.toFixed(2)}</b> y <b>$${item.max.toFixed(2)}</b>.<br>
          Se encontraron <b>${item.count}</b> registros vinculados.
        `,
        icon: 'info',
        confirmButtonText: 'Cerrar'
      });
    }
  }
};
</script>

<style scoped>
.breadcrumb-container {
  font-size: 1.1rem;
  color: #555;
}

.spinner {
  animation: rotate 1.2s linear infinite;
}

@keyframes rotate {
  100% { transform: rotate(360deg); }
}

.table {
  font-size: 0.92rem;
}

.badge {
  font-size: 0.85em;
}
</style>